import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarDays, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  Users,
  Calendar,
  Tag
} from 'lucide-react';
import { EventSeries } from '../../types/event';
import seriesAPI, { SeriesFilters } from '../../services/seriesAPI';
import categoryAPI from '../../services/categoryAPI';
import Button from '../../components/UI/Button';
import { logError } from '../../utils/errorLogger';
import Input from '../../components/UI/Input';
import Toast from '../../components/UI/Toast';
import Layout from '../../components/Layout/Layout';
import { useAppSelector } from '../../store/hooks';
import { useAlert } from '../../contexts';

const SeriesListPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { showConfirm } = useAlert();
  
  // State
  const [series, setSeries] = useState<EventSeries[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<SeriesFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadSeries = useCallback(async () => {
    try {
      setLoading(true);
      const seriesData = await seriesAPI.getSeries(filters);
      setSeries(seriesData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load series');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load data
  useEffect(() => {
    loadSeries();
    loadCategories();
  }, [filters, loadSeries]);

  const loadCategories = async () => {
    try {
      const categoriesData = await categoryAPI.getCategories();
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (err) {
      logError('Failed to load categories', err);
    }
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchTerm });
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof SeriesFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Handle series deletion
  const handleDeleteSeries = async (seriesId: string) => {
    const confirmed = await showConfirm('Are you sure you want to delete this series? All events in this series will be deleted.');
    if (!confirmed) {
      return;
    }

    try {
      await seriesAPI.deleteSeries(seriesId);
      setSuccess('Series deleted successfully');
      loadSeries(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete series');
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      planning: { label: 'Planning', className: 'bg-gray-100 text-gray-800' },
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      completed: { label: 'Completed', className: 'bg-blue-100 text-blue-800' },
      canceled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.planning;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <CalendarDays className="h-7 w-7 mr-3 text-mirsal-green" />
                Event Series
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your event series and view all related events
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Link to={`/${user?.role}/events/create-series`}>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create Series
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search series by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                className="flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </Button>
              <Button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
              </Button>
            </div>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={filters.status?.[0] || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value ? [e.target.value] : undefined)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm"
                  >
                    <option value="">All Statuses</option>
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="canceled">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={filters.category_id || ''}
                    onChange={(e) => handleFilterChange('category_id', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={clearFilters}
                    variant="outline"
                    className="w-full"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Series List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mirsal-green mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading series...</p>
          </div>
        ) : series.length === 0 ? (
          <div className="text-center py-12">
            <CalendarDays className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No series found</h3>
            <p className="text-gray-600 mb-6">
              {Object.keys(filters).length > 0 
                ? 'No series match your current filters. Try adjusting your search criteria.'
                : 'You haven\'t created any event series yet.'
              }
            </p>
            <Link to={`/${user?.role}/events/create-series`}>
              <Button className="flex items-center gap-2 mx-auto">
                <Plus className="h-4 w-4" />
                Create Your First Series
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {series.map((seriesItem) => (
              <div
                key={seriesItem.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Series Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {seriesItem.name}
                      </h3>
                      <div className="flex items-center space-x-3 mt-2">
                        {seriesItem.category_name && (
                          <div className="flex items-center">
                            <Tag className="h-3 w-3 mr-1 text-gray-400" />
                            <span className="text-sm text-gray-500">{seriesItem.category_name}</span>
                          </div>
                        )}
                        {getStatusBadge(seriesItem.status)}
                      </div>
                    </div>
                  </div>

                  {/* Series Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-mirsal-green">
                        {seriesItem.total_events || 0}
                      </div>
                      <div className="text-xs text-gray-500">Events</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {seriesItem.overall_guest_limit || '∞'}
                      </div>
                      <div className="text-xs text-gray-500">Max Guests</div>
                    </div>
                  </div>

                  {/* Series Description */}
                  {seriesItem.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {seriesItem.description}
                    </p>
                  )}

                  {/* Series Info */}
                  <div className="space-y-2 text-sm text-gray-500">
                    {seriesItem.series_date_range && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>{seriesItem.series_date_range}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      <span>
                        {seriesItem.allow_companions ? 'Companions allowed' : 'No companions'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <Link to={`/${user?.role}/series/${seriesItem.id}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                    </Link>
                    
                    <div className="flex items-center space-x-2">
                      <Link to={`/${user?.role}/series/${seriesItem.id}/edit`}>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </Link>
                      
                      {(user?.role === 'admin' || seriesItem.host_name === `${user?.first_name} ${user?.last_name}`) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteSeries(seriesItem.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Toast Messages */}
        {error && (
          <Toast
            type="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}
        
        {success && (
          <Toast
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default SeriesListPage;
